import moment from 'moment';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { Form, Icon as LegacyIcon } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, DatePicker, InputNumber, Select, Spin, Radio, Checkbox } from 'antd';
import OWButton from '../ow_button/OWButton';
import BackButton from '../back_button/BackButton';
import {
	PROJECT_BUDGET_TYPES,
	PROJECT_ENTITY_TYPES,
	PROJECT_ENTITY_TYPE_OPTIONS,
} from '../../constants/ProjectEntities';
import { isProjectsV2Enabled } from '../../utils/FeatureFlagUtils';
import * as React from 'react';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import {
	supplierFacilitiesRestCrudThunksForBuyer,
	supplierFacilitiesRestCrudThunksForSupplier,
} from '../../thunks/supplier_facilities_thunks';
import {
	suppliersRestCrudThunksForBuyer,
	suppliersRestCrudThunksForSupplier,
} from '../../thunks/suppliers_thunks';
import {
	projectsRestCrudThunksForBuyer,
	projectsRestCrudThunksForSupplier,
} from '../../thunks/projects_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import {
	projectTemplatesRestCrudThunksForBuyer,
	projectTemplatesRestCrudThunksForSupplier,
} from '../../thunks/project_template_thunks';
import { debounce } from '../../utils/PerformanceUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import ClearErrors from '../clear_errors/ClearErrors';
import { PROJECTS_CRUD_ACTION_CREATORS } from '../../actions/projects_actions';

const PROJECT_FORM_PROJECT_TEMPLATES_AUTOCOMPLETE = 'PROJECT_FORM_PROJECT_TEMPLATES_AUTOCOMPLETE';

interface ProjectFormProps extends FormComponentProps {
	history: any;
	onSubmit: any;
	onCancel: any;
	redirectForwardUrl?: string;
	onSuccess?: any;
	isApprovalForm?: any;
	creating: boolean;
	updating: boolean;
	formData: any;
	createErrors: any[];
	updateErrors: any[];
	fetchBuyer: any;
	currentUser: any;
	updateProject: any;
	fetchSupplierFacility: any;
	fetchSupplierCompany: any;
	fetchProjectTemplate: any;
	fetchProjectTemplates: any;
	projectTemplates: any;
	projectTemplatesFetching: any;
}

class ProjectEditForm extends React.Component<ProjectFormProps, any> {
	constructor(props) {
		super(props);
		this.state = {
			previewVisible: false,
			uploadLoading: false,
			privateSupplier: false,
			budget: 0,
			title: '',
			projectTemplate: {},
			buttonLoading: false,
		};
	}

	componentDidMount() {
		const { formData, currentUser, fetchProjectTemplate, fetchProjectTemplates } = this.props;
		const projectTemplateId = formData.projectTemplateId;
		const userType = nullSafeGetOrElse('userType', currentUser, 'buyer');
		fetchProjectTemplates(userType, { limit: 50, offset: 0 });
		if (projectTemplateId) {
			fetchProjectTemplate(userType, projectTemplateId).then((lt) =>
				this.setState({ projectTemplate: lt })
			);
		}
	}

	handleSubmit = (e) => {
		const { formData, form, history, redirectForwardUrl, onSuccess, updateProject, currentUser } =
			this.props;
		const { projectTemplate } = this.state;
		e.preventDefault();
		form.validateFields((err, values) => {
			if (!err) {
				const requiredFieldDetails = nullSafeGetOrElse('requiredFields', projectTemplate, []).map(
					(rf) => ({
						key: form.getFieldValue(`requiredFields-${rf.key}-key`),
						valueType: form.getFieldValue(`requiredFields-${rf.key}-valueType`),
						valueAsString: form.getFieldValue(`requiredFields-${rf.key}-valueAsString`),
					})
				);

				const optionalFieldDetails = nullSafeGetOrElse('optionalFields', projectTemplate, [])
					.map((of) => ({
						key: form.getFieldValue(`optionalFields-${of.key}-key`),
						valueType: form.getFieldValue(`optionalFields-${of.key}-valueType`),
						valueAsString: form.getFieldValue(`optionalFields-${of.key}-valueAsString`),
					}))
					.filter((t) => t.valueAsString !== undefined && t.valueAsString !== '');
				const projectTemplateDetails = requiredFieldDetails.concat(optionalFieldDetails);
				const hasBudget = values.hasBudget === 'yes';
				const isTotalBudget = hasBudget && values.budgetType === PROJECT_BUDGET_TYPES.total;
				let project = {
					...formData,
					id: values.id,
					title: values.title,
					description: values.additionalInfo,
					// projects-v2 adds start/end dates, budget type, and entities; the
					// legacy form only had a due date + budget.
					...(isProjectsV2Enabled()
						? {
								startDate: values.startDate,
								endDate: values.endDate,
								dueDate: values.endDate,
								// Send explicit null (not undefined) when clearing — undefined is
								// dropped by JSON.stringify, so the backend would keep the old budget.
								budgetType: hasBudget ? values.budgetType : null,
								budget: isTotalBudget ? values.budget : null,
								entities: values.entities,
						  }
						: {
								dueDate: values.dueDate,
								budget: values.budget,
						  }),
					buyerCompanyId: values.buyerCompanyId,
					createdBy: values.createdBy,
					projectTemplateId: values.projectTemplateId
						? parseInt(values.projectTemplateId, 10)
						: undefined,
					projectTemplateDetails: projectTemplateDetails,
				};
				const userType = nullSafeGetOrElse('userType', currentUser, 'buyer');
				this.setState({ buttonLoading: true });
				updateProject(userType, project)
					.then((record) => {
						if (redirectForwardUrl) {
							history.push(redirectForwardUrl);
						}
						if (onSuccess) {
							onSuccess(record);
						}
					})
					.finally(() => {
						this.setState({ buttonLoading: false });
					});
			}
		});
	};
	searchProjectTemplates = (searchText) => {
		this.debouncedSearchProjectTemplates(searchText);
	};
	debouncedSearchProjectTemplates = debounce(
		(searchText) => {
			const { fetchProjectTemplates, currentUser } = this.props;
			const userType = nullSafeGetOrElse('userType', currentUser, 'buyer');
			fetchProjectTemplates(userType, { name: searchText && searchText.trim() });
		},
		500,
		false
	);

	handleProjectTemplateChange = (projectTemplateId) => {
		const { fetchProjectTemplate, currentUser } = this.props;
		const userType = nullSafeGetOrElse('userType', currentUser, 'buyer');
		if (projectTemplateId) {
			fetchProjectTemplate(userType, projectTemplateId).then((lt) =>
				this.setState({ projectTemplate: lt })
			);
		}
	};

	onSuccess = (record) => {
		this.setState({
			title: nullSafeGet('title', record),
			description: nullSafeGet('description', record),
			budget: nullSafeGet('budget', record),
		});
	};

	handleCancel = () => this.setState({ previewVisible: false });

	render() {
		const {
			createErrors,
			updateErrors,
			creating,
			isApprovalForm,
			updating,
			form,
			formData,
			currentUser,
			projectTemplates,
			projectTemplatesFetching,
		} = this.props;
		const { projectTemplate, buttonLoading } = this.state;

		const { getFieldDecorator } = form;
		const submitLabel = isApprovalForm ? 'Approve project' : 'Update project';
		const projectTemplatesDropdownRecords = getRecordsForTargetCollection(
			projectTemplates,
			PROJECT_FORM_PROJECT_TEMPLATES_AUTOCOMPLETE
		);

		let initialProjectDetailsMap = {};
		if (formData.projectTemplateDetails) {
			formData.projectTemplateDetails.map((ltd) => {
				initialProjectDetailsMap[`requiredFields-${ltd.key}-key`] = ltd.key;
				initialProjectDetailsMap[`requiredFields-${ltd.key}-valueType`] = ltd.valueType;
				initialProjectDetailsMap[`requiredFields-${ltd.key}-valueAsString`] = ltd.valueAsString;
				initialProjectDetailsMap[`optionalFields-${ltd.key}-key`] = ltd.key;
				initialProjectDetailsMap[`optionalFields-${ltd.key}-valueType`] = ltd.valueType;
				initialProjectDetailsMap[`optionalFields-${ltd.key}-valueAsString`] = ltd.valueAsString;
			});
		}

		const getRequiredFieldItems = (lt) => {
			if (!lt || !lt.id) {
				return null;
			}
			return lt.requiredFields.map((field) => {
				getFieldDecorator(`requiredFields-${field.key}-key`, { initialValue: field.key });
				getFieldDecorator(`requiredFields-${field.key}-valueType`, { initialValue: field.type });
				return (
					<Form.Item label={field.key}>
						{form.getFieldDecorator(`requiredFields-${field.key}-valueAsString`, {
							validateTrigger: ['onChange', 'onBlur'],
							initialValue: initialProjectDetailsMap[`requiredFields-${field.key}-valueAsString`],
							rules: [{ required: true, message: 'This field is required.' }],
						})(<Input style={{ maxWidth: 640 }} />)}
					</Form.Item>
				);
			});
		};

		const getOptionalFieldItems = (lt) => {
			if (!lt || !lt.id) {
				return null;
			}
			return lt.optionalFields.map((field) => {
				getFieldDecorator(`optionalFields-${field.key}-key`, { initialValue: field.key });
				getFieldDecorator(`optionalFields-${field.key}-valueType`, { initialValue: field.type });
				return (
					<Form.Item label={field.key}>
						{form.getFieldDecorator(`optionalFields-${field.key}-valueAsString`, {
							validateTrigger: ['onChange', 'onBlur'],
							initialValue: initialProjectDetailsMap[`requiredFields-${field.key}-valueAsString`],
						})(<Input style={{ maxWidth: 640 }} />)}
					</Form.Item>
				);
			});
		};

		getFieldDecorator('id', { initialValue: formData.id });
		getFieldDecorator('createdAt', { initialValue: formData.createdAt });
		getFieldDecorator('description', { initialValue: formData.description });
		getFieldDecorator('buyerCompanyId', {
			initialValue: formData.buyerCompanyId || nullSafeGet('facility.buyerCompanyId', currentUser),
		});
		getFieldDecorator('createdBy', {
			initialValue: formData.createdBy || nullSafeGet('email', currentUser),
		});
		getFieldDecorator('budget', {
			initialValue: formData.budget,
		});
		// Seed "has a budget?" from the saved project so budget-less projects don't
		// open with Yes selected (which forces a required Budget field on an unrelated edit).
		const hasBudgetDefault = formData.budget != null || formData.budgetType ? 'yes' : 'no';
		const hasBudget = form.getFieldValue('hasBudget') || hasBudgetDefault;
		const budgetType =
			form.getFieldValue('budgetType') || formData.budgetType || PROJECT_BUDGET_TYPES.total;
		// Every project has work orders — that entity is locked on. Individual
		// budget is per-deliverable, so it's only available with Deliverables.
		const entities =
			form.getFieldValue('entities') ||
			nullSafeGetOrElse('entities', formData, [PROJECT_ENTITY_TYPES.workOrders]);
		const hasDeliverables = entities.includes(PROJECT_ENTITY_TYPES.deliverables);
		const entityOptions = PROJECT_ENTITY_TYPE_OPTIONS.map((option) =>
			option.value === PROJECT_ENTITY_TYPES.workOrders ? { ...option, disabled: true } : option
		);
		// projects-v2 replaces the legacy Due date + Budget fields with start/end
		// dates, budget type, and entities.
		const projectsV2 = isProjectsV2Enabled();

		//TODO: UPGRADE FORM
		return (
			<Form layout="vertical" onSubmit={this.handleSubmit} className="supplierForm">
				<ClearErrors actionCreators={PROJECTS_CRUD_ACTION_CREATORS} />
				<Form.Item label="Title">
					{getFieldDecorator('title', {
						initialValue: formData.title,
						rules: [{ required: true, message: 'This field is required.' }],
					})(<Input style={{ maxWidth: 640 }} />)}
				</Form.Item>
				<Form.Item label="Scope">
					{getFieldDecorator('additionalInfo', {
						initialValue: formData.description,
					})(
						<Input.TextArea
							style={{ maxWidth: 640 }}
							autoSize={{ minRows: 3 }}
							placeholder="Describe the project's scope — what's included, goals, or any context"
						/>
					)}
				</Form.Item>
				{projectsV2 ? (
					<div style={{ display: 'flex', gap: 16, maxWidth: 640 }}>
						<Form.Item label="Start Date" style={{ flex: 1 }}>
							{getFieldDecorator('startDate', {
								// Fall back to the legacy dueDate so a legacy project (which only has
								// dueDate) can be saved without forcing a brand-new start date.
								initialValue: formData.startDate
									? moment(formData.startDate)
									: formData.dueDate
									? moment(formData.dueDate)
									: undefined,
								rules: [{ required: true, message: 'This field is required.' }],
							})(<DatePicker style={{ width: '100%' }} format="MMM D, YYYY" />)}
						</Form.Item>
						<Form.Item label="End Date (optional)" style={{ flex: 1 }}>
							{getFieldDecorator('endDate', {
								initialValue: formData.endDate
									? moment(formData.endDate)
									: formData.dueDate
									? moment(formData.dueDate)
									: undefined,
							})(<DatePicker style={{ width: '100%' }} format="MMM D, YYYY" />)}
						</Form.Item>
					</div>
				) : (
					<Form.Item label="Due date">
						{getFieldDecorator('dueDate', {
							initialValue: formData.dueDate ? moment(formData.dueDate) : undefined,
							rules: [{ required: true, message: 'This field is required.' }],
						})(
							// Keep the time-of-day picker so editing doesn't rewrite the due date to 00:00.
							<DatePicker
								style={{ width: '100%', maxWidth: 640 }}
								format="MMM D, YYYY h:mm A"
								showTime
							/>
						)}
					</Form.Item>
				)}
				{projectsV2 ? (
					<>
						<Form.Item label="Does this project have a budget?">
							{getFieldDecorator('hasBudget', { initialValue: hasBudgetDefault })(
								<Radio.Group>
									<Radio value="yes">Yes</Radio>
									<Radio value="no">No</Radio>
								</Radio.Group>
							)}
						</Form.Item>
						{hasBudget === 'yes' ? (
							<Form.Item>
								{getFieldDecorator('budgetType', {
									initialValue: formData.budgetType || PROJECT_BUDGET_TYPES.total,
								})(
									<Radio.Group>
										<div className="flex flex-col gap-3">
											<div>
												<Radio value={PROJECT_BUDGET_TYPES.total}>Total Budget</Radio>
												<div
													className="footnoteregular text-character-secondary"
													style={{ marginLeft: 24 }}
												>
													Budget allocated for the entire project.
												</div>
											</div>
											<div>
												<Radio value={PROJECT_BUDGET_TYPES.individual} disabled={!hasDeliverables}>
													Individual Budget
												</Radio>
												<div
													className="footnoteregular text-character-secondary"
													style={{ marginLeft: 24 }}
												>
													Each deliverable created will have its own budget
													{hasDeliverables ? '' : ' — enable Deliverables to use this'}
												</div>
											</div>
										</div>
									</Radio.Group>
								)}
							</Form.Item>
						) : null}
						{hasBudget === 'yes' && budgetType === PROJECT_BUDGET_TYPES.total ? (
							<Form.Item label="Budget">
								{getFieldDecorator('budget', {
									initialValue: formData.budget,
									rules: [{ required: true, message: 'This field is required.' }],
								})(
									<InputNumber
										min={0}
										style={{ display: 'flex', width: '100%', maxWidth: 640 }}
										addonAfter={nullSafeGetOrElse('id', getCurrency({ currentUser }), 'USD')}
									/>
								)}
							</Form.Item>
						) : null}
					</>
				) : (
					<Form.Item label="Budget">
						{getFieldDecorator('budget', {
							initialValue: formData.budget,
							rules: [{ required: true, message: 'This field is required.' }],
						})(
							<InputNumber
								min={0}
								style={{ display: 'flex', width: '100%', maxWidth: 640 }}
								addonAfter={nullSafeGetOrElse('id', getCurrency({ currentUser }), 'USD')}
							/>
						)}
					</Form.Item>
				)}
				<Form.Item label="Project Template">
					{getFieldDecorator('projectTemplateId', {
						initialValue: formData.projectTemplateId,
					})(
						<Select
							notFoundContent={projectTemplatesFetching ? <Spin size="small" /> : null}
							placeholder="Type to search for project template"
							filterOption={false}
							showSearch={true}
							onSearch={this.searchProjectTemplates}
							onChange={this.handleProjectTemplateChange}
							style={{ maxWidth: 640 }}
						>
							{projectTemplatesDropdownRecords.map((d) => (
								<Select.Option value={d.id} key={d.id}>
									{d.name}
								</Select.Option>
							))}
						</Select>
					)}
				</Form.Item>
				{getRequiredFieldItems(projectTemplate)}
				{getOptionalFieldItems(projectTemplate)}
				{projectsV2 ? (
					<Form.Item label="Project Entities" required>
						{getFieldDecorator('entities', {
							initialValue: nullSafeGetOrElse('entities', formData, [
								PROJECT_ENTITY_TYPES.workOrders,
							]),
							rules: [{ required: true, message: 'Select at least one entity.' }],
						})(
							<Checkbox.Group
								options={entityOptions}
								onChange={(checked) => {
									// Individual budget requires Deliverables — if it's removed, snap the
									// budget type back to Total so we never save individual-with-no-deliverables.
									if (
										!checked.includes(PROJECT_ENTITY_TYPES.deliverables) &&
										form.getFieldValue('budgetType') === PROJECT_BUDGET_TYPES.individual
									) {
										form.setFieldsValue({ budgetType: PROJECT_BUDGET_TYPES.total });
									}
								}}
							/>
						)}
					</Form.Item>
				) : null}
				<Form.Item>
					<OWButton
						type="primary"
						htmlType="submit"
						loading={updating || buttonLoading}
						className="contactForm__button"
					>
						{submitLabel}
					</OWButton>
					<span style={{ marginLeft: '16px' }}>
						<BackButton buttonText="Cancel" />
					</span>
				</Form.Item>
			</Form>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	onCancel: ownProps.onCancel,
	onSubmit: ownProps.onSubmit,
	redirectForwardUrl: ownProps.redirectForwardUrl,
	onSuccess: ownProps.onSuccess,
	projectTemplatesFetching: state.project_templates.fetching,
	projectTemplates: state.project_templates,
	isApprovalForm: ownProps.isApprovalForm,
	createErrors: state.work_orders.createErrors,
	updateErrors: state.work_orders.updateErrors,
	formData: ownProps.formData,
	creating: state.work_orders.creating,
	updating: state.work_orders.updating,
	supplierFacilities: state.supplier_facilities,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
	updateProject: (userType, project) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectsRestCrudThunksForSupplier.update(project)
				: projectsRestCrudThunksForBuyer.update(project)
		),
	fetchProjectTemplate: (userType, id) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectTemplatesRestCrudThunksForSupplier.readOne(id)
				: projectTemplatesRestCrudThunksForBuyer.readOne(id)
		),
	fetchProjectTemplates: (userType, params) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectTemplatesRestCrudThunksForSupplier.read(
						params,
						PROJECT_FORM_PROJECT_TEMPLATES_AUTOCOMPLETE
				  )
				: projectTemplatesRestCrudThunksForBuyer.read(
						params,
						PROJECT_FORM_PROJECT_TEMPLATES_AUTOCOMPLETE
				  )
		),
	fetchSupplierFacility: (userType, id) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? supplierFacilitiesRestCrudThunksForSupplier.readOne(id)
				: supplierFacilitiesRestCrudThunksForBuyer.readOne(id)
		),
	fetchSupplierCompany: (userType, id) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? suppliersRestCrudThunksForSupplier.readOne(id)
				: suppliersRestCrudThunksForBuyer.readOne(id)
		),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(Form.create<ProjectFormProps>()(ProjectEditForm))
);
